/**
 * PLAYER module — keyboard state tracking and pointer-lock management.
 * Owned by PLAYER; see CONTRACTS.md.
 */

export interface KeyboardHandle {
  isDown(code: string): boolean
  dispose(): void
}

/** Tracks held keys by KeyboardEvent.code; clears on window blur. */
export const createKeyboard = (): KeyboardHandle => {
  const down = new Set<string>()

  const onKeyDown = (event: KeyboardEvent): void => {
    down.add(event.code)
  }
  const onKeyUp = (event: KeyboardEvent): void => {
    down.delete(event.code)
  }
  const onBlur = (): void => {
    down.clear()
  }

  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('keyup', onKeyUp)
  window.addEventListener('blur', onBlur)

  return {
    isDown: (code) => down.has(code),
    dispose: () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
    }
  }
}

export interface PointerLockHandle {
  isLocked(): boolean
  request(): void
  exit(): void
  dispose(): void
}

/**
 * Manages pointer lock on the render canvas and forwards mouse deltas while
 * locked. requestPointerLock may reject (no user gesture, vendor quirks) —
 * failures are swallowed; the canvas-click re-acquire path recovers.
 */
export const createPointerLock = (
  getElement: () => Element | null,
  onMouseDelta: (dx: number, dy: number) => void
): PointerLockHandle => {
  const isLocked = (): boolean => {
    const element = getElement()
    return element !== null && document.pointerLockElement === element
  }

  const onMouseMove = (event: MouseEvent): void => {
    if (!isLocked()) return
    onMouseDelta(event.movementX, event.movementY)
  }

  document.addEventListener('mousemove', onMouseMove)

  const request = (): void => {
    const element = getElement()
    if (!element || document.pointerLockElement === element) return
    try {
      const result = (element as HTMLElement).requestPointerLock() as unknown
      if (result instanceof Promise) {
        result.catch(() => undefined)
      }
    } catch {
      // Pointer lock denied — canvas click will retry.
    }
  }

  const exit = (): void => {
    if (document.pointerLockElement) {
      document.exitPointerLock()
    }
  }

  return {
    isLocked,
    request,
    exit,
    dispose: () => {
      document.removeEventListener('mousemove', onMouseMove)
    }
  }
}
